import { eventBus } from '../utils/EventEmitter.js';
import { generateFileName, downloadBlob } from '../utils/file.js';

/**
 * Manages image and GIF exports
 */
export class ExportManager {
    constructor(canvasManager, uiManager) {
        this.canvasManager = canvasManager;
        this.uiManager = uiManager;
        
        // Fixed export dimensions
        this.gifMaxWidth = 800;   // Max width for GIFs
        this.gifMaxHeight = 600;  // Max height for GIFs
        this.exportWidth = 1920;  // For videos
        this.exportHeight = 1080; // For videos
        
        // Export settings
        this.fps = 15;            // Fixed 15fps
        this.duration = 3.0;      // 3 seconds duration
        
        // Create canvases for GIF handling
        this.baseCanvas = document.createElement('canvas');
        this.currentCanvas = document.createElement('canvas');
        this.baseCtx = this.baseCanvas.getContext('2d');
        this.currentCtx = this.currentCanvas.getContext('2d');
        
        // Enable smooth scaling
        this.baseCtx.imageSmoothingEnabled = true;
        this.currentCtx.imageSmoothingEnabled = true;
        
        // Word list for random filenames
        this.words = [
            'glitch', 'pixel', 'cyber', 'neon', 'retro', 'wave', 'synth', 'vapor',
            'digital', 'static', 'noise', 'chaos', 'drift', 'pulse', 'signal', 'data',
            'binary', 'matrix', 'crypto', 'quantum', 'neural', 'cosmic', 'atomic', 'hyper',
            'sonic', 'laser', 'plasma', 'vector', 'circuit', 'techno', 'future', 'chrome'
        ];
        
        this.setupEventListeners();
    }

    /**
     * Generate a filename with random words and timestamp
     */
    generateFileName(extension) {
        // Get 3 random words
        const randomWords = Array.from({ length: 3 }, () => 
            this.words[Math.floor(Math.random() * this.words.length)]
        );
        
        // Generate timestamp in format YYYYMMDD-HHMMSS
        const now = new Date();
        const timestamp = now.toISOString()
            .replace(/[^0-9]/g, '')
            .slice(0, 14);
        
        return `${randomWords.join('-')}-${timestamp}.${extension}`;
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
        // Mobile download options
        const downloadOptions = document.querySelectorAll('.download-option');
        
        // Wire up download options
        downloadOptions.forEach(option => {
            option.addEventListener('click', () => {
                const format = option.dataset.format;
                if (format === 'gif') {
                    this.exportGif();
                } else if (format === 'video') {
                    this.exportVideo();
                }
                // Hide the modal after selecting an option
                const modalOverlay = document.querySelector('.download-modal-overlay');
                if (modalOverlay) {
                    modalOverlay.classList.remove('show');
                }
            });
        });

        // Handle download button click
        const downloadButton = document.getElementById('downloadButton');
        if (downloadButton) {
            downloadButton.addEventListener('click', () => {
                const modalOverlay = document.querySelector('.download-modal-overlay');
                if (modalOverlay) {
                    modalOverlay.classList.add('show');
                }
            });
        }

        // Close modal when clicking outside
        const modalOverlay = document.querySelector('.download-modal-overlay');
        if (modalOverlay) {
            modalOverlay.addEventListener('click', (e) => {
                if (e.target === modalOverlay) {
                    modalOverlay.classList.remove('show');
                }
            });
        }

        // Close modal on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                const modalOverlay = document.querySelector('.download-modal-overlay');
                if (modalOverlay && modalOverlay.classList.contains('show')) {
                    modalOverlay.classList.remove('show');
                }
            }
        });
    }

    /**
     * Clean up resources
     */
    cleanup() {
        // Clear canvases
        this.baseCanvas.width = 0;
        this.baseCanvas.height = 0;
        this.currentCanvas.width = 0;
        this.currentCanvas.height = 0;
    }

    /**
     * Get video export dimensions
     */
    getVideoExportDimensions() {
        const canvas = this.canvasManager.getCanvas();
        const aspectRatio = canvas.width / canvas.height;
        let width, height;
        
        if (aspectRatio > 16/9) {
            width = this.exportWidth;
            height = Math.floor(width / aspectRatio);
        } else {
            height = this.exportHeight;
            width = Math.floor(height * aspectRatio);
        }
        
        // Ensure dimensions are even
        width = Math.floor(width / 2) * 2;
        height = Math.floor(height / 2) * 2;
        
        return { width, height };
    }

    /**
     * Export as video
     */
    async exportVideo() {
        console.log('Starting video export...');
        
        // Show progress bar at the start
        this.uiManager.showProgress('Initializing video export...');

        try {
            // Get proper dimensions for video export
            const { width, height } = this.getVideoExportDimensions();
            
            // Create a temporary canvas for video export
            const exportCanvas = document.createElement('canvas');
            exportCanvas.width = width;
            exportCanvas.height = height;
            const exportCtx = exportCanvas.getContext('2d');

            // First capture as WebM
            this.uiManager.updateProgress(0, 'Recording WebM...');
            const webmBlob = await new Promise((resolve, reject) => {
                try {
                    const stream = exportCanvas.captureStream(30); // 30 FPS
                    const recorder = new MediaRecorder(stream, {
                        mimeType: 'video/webm;codecs=vp9',
                        videoBitsPerSecond: 5000000 // 5 Mbps
                    });

                    const chunks = [];
                    recorder.ondataavailable = (e) => chunks.push(e.data);
                    recorder.onstop = () => {
                        const blob = new Blob(chunks, { type: 'video/webm' });
                        stream.getTracks().forEach(track => track.stop());
                        resolve(blob);
                    };
                    recorder.onerror = reject;

                    // Start recording
                    recorder.start();
                    const startTime = performance.now();
                    const duration = 10000; // 10 seconds
                    const frameInterval = 1000 / 30; // 30fps
                    let lastFrameTime = startTime;
                    let frameCount = 0;

                    const updateFrame = () => {
                        const now = performance.now();
                        const elapsed = now - startTime;
                        
                        if (elapsed >= duration) {
                            recorder.stop();
                            return;
                        }

                        // Only render if it's time for a new frame
                        if (now - lastFrameTime >= frameInterval) {
                            // Draw current frame
                            exportCtx.drawImage(this.canvasManager.getCanvas(), 0, 0, width, height);
                            lastFrameTime = now;
                            frameCount++;
                        }

                        const progress = (elapsed / duration) * (typeof FFmpeg !== 'undefined' ? 50 : 100);
                        this.uiManager.updateProgress(Math.round(progress), `Recording: ${Math.round(progress)}%`);
                        requestAnimationFrame(updateFrame);
                    };

                    requestAnimationFrame(updateFrame);
                } catch (e) {
                    reject(e);
                }
            });

            // Try to convert to MP4 if FFmpeg is available
            try {
                if (typeof FFmpeg !== 'undefined') {
                    this.uiManager.updateProgress(50, 'Converting to MP4...');
                    const ffmpeg = FFmpeg.createFFmpeg({ 
                        log: true,
                        logger: ({ message }) => {
                            console.log('FFmpeg:', message);
                            const frameMatch = message.match(/frame=\s*(\d+)/);
                            if (frameMatch) {
                                const currentFrame = parseInt(frameMatch[1]);
                                const progress = 50 + ((currentFrame / 300) * 50);
                                this.uiManager.updateProgress(Math.round(progress), `Converting: ${Math.round(progress)}%`);
                            }
                        }
                    });

                    await ffmpeg.load();
                    await ffmpeg.FS('writeFile', 'input.webm', new Uint8Array(await webmBlob.arrayBuffer()));
                    await ffmpeg.run('-i', 'input.webm', '-c:v', 'libx264', '-preset', 'fast', '-crf', '22', '-movflags', '+faststart', 'output.mp4');

                    const mp4Data = ffmpeg.FS('readFile', 'output.mp4');
                    const mp4Blob = new Blob([mp4Data.buffer], { type: 'video/mp4' });

                    ffmpeg.FS('unlink', 'input.webm');
                    ffmpeg.FS('unlink', 'output.mp4');

                    // Track successful export
                    this.trackExport('complete_export', {
                        label: 'MP4',
                        value: Math.round(mp4Blob.size / 1024)
                    });

                    // Download MP4
                    downloadBlob(mp4Blob, this.generateFileName('mp4'));
                } else {
                    // Fallback to WebM if FFmpeg is not available
                    console.log('FFmpeg not available, falling back to WebM export');
                    downloadBlob(webmBlob, this.generateFileName('webm'));
                }

                this.uiManager.hideProgress();
            } catch (ffmpegError) {
                console.warn('FFmpeg conversion failed, falling back to WebM:', ffmpegError);
                downloadBlob(webmBlob, this.generateFileName('webm'));
                this.uiManager.hideProgress();
            }
        } catch (error) {
            console.error('Video export error:', error);
            
            // Track export error
            this.trackExport('export_error', {
                label: error.message,
                value: 0
            });
            
            this.uiManager.hideProgress();
            alert('Failed to export video: ' + error.message);
        }
    }

    /**
     * Export as GIF
     */
    async exportGif() {
        try {
            console.log('Starting GIF export...');
            
            if (!this.canvasManager) {
                throw new Error('Canvas manager not initialized');
            }
            
            const canvas = this.canvasManager.getCanvas();
            if (!canvas) {
                throw new Error('Canvas not available');
            }

            // Show progress bar
            this.uiManager.showProgress('Preparing GIF...');

            // Check if current image is animated
            const isAnimated = this.canvasManager.currentGif !== null;

            const aspectRatio = canvas.width / canvas.height;
            
            // Calculate dimensions that maintain aspect ratio
            let gifWidth, gifHeight;
            
            if (aspectRatio > 1) {
                gifWidth = Math.min(this.gifMaxWidth, canvas.width);
                gifHeight = Math.floor(gifWidth / aspectRatio);
                if (gifHeight > this.gifMaxHeight) {
                    gifHeight = this.gifMaxHeight;
                    gifWidth = Math.floor(gifHeight * aspectRatio);
                }
            } else {
                gifHeight = Math.min(this.gifMaxHeight, canvas.height);
                gifWidth = Math.floor(gifHeight * aspectRatio);
                if (gifWidth > this.gifMaxWidth) {
                    gifWidth = this.gifMaxWidth;
                    gifHeight = Math.floor(gifWidth / aspectRatio);
                }
            }
            
            // Ensure dimensions are even
            gifWidth = Math.floor(gifWidth / 2) * 2;
            gifHeight = Math.floor(gifHeight / 2) * 2;

            // Create temporary canvas for proper scaling
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = gifWidth;
            tempCanvas.height = gifHeight;
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.imageSmoothingEnabled = true;

            // Create GIF encoder
            const gif = new window.GIF({
                workers: navigator.hardwareConcurrency || 4,
                quality: 20,
                width: gifWidth,
                height: gifHeight,
                workerScript: '/js/gif.worker.js',
                repeat: 0,
                dither: 'FloydSteinberg'
            });
            
            gif.on('progress', (p) => {
                const progress = Math.round(p * 100);
                this.uiManager.updateProgress(progress, `Processing: ${progress}%`);
            });
            
            gif.on('finished', (blob) => {
                // Clean up temporary canvas
                tempCanvas.width = 0;
                tempCanvas.height = 0;
                
                // Download the GIF
                downloadBlob(blob, this.generateFileName('gif'));
                this.uiManager.hideProgress();
            });

            // Capture frames
            const duration = 3000; // 3 seconds
            const startTime = performance.now();
            let frameCount = 0;
            const frameDelay = 66; // ~15fps
            
            const captureFrame = async () => {
                tempCtx.clearRect(0, 0, gifWidth, gifHeight);
                tempCtx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, gifWidth, gifHeight);
                
                gif.addFrame(tempCanvas, { 
                    copy: true, 
                    delay: frameDelay
                });
                
                const elapsed = performance.now() - startTime;
                const progress = (elapsed / duration) * 50;
                this.uiManager.updateProgress(progress, `Capturing frame ${++frameCount}...`);
                
                if (elapsed < duration) {
                    await new Promise(resolve => setTimeout(resolve, frameDelay));
                    requestAnimationFrame(captureFrame);
                } else {
                    this.uiManager.updateProgress(50, 'Rendering GIF...');
                    gif.render();
                }
            };
            
            requestAnimationFrame(captureFrame);
        } catch (error) {
            console.error('Failed to export GIF:', error);
            this.uiManager.hideProgress();
            alert('Failed to export GIF: ' + error.message);
            
            // Track export error
            this.trackExport('export_error', {
                label: error.message,
                value: 0
            });
        }
    }

    /**
     * Track export metrics
     */
    trackExport(type, data) {
        if (typeof gtag !== 'undefined') {
            gtag('event', type, {
                'event_category': 'Export',
                'event_label': data.label,
                'value': data.value
            });
        }
    }
} 